import * as React from 'react';

import { Button, FloatingToolbar } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';
import { Download, Move, Trash2, X, CheckSquare } from 'lucide-react';

import { $bulkDownloadFiles } from '../../_state/fileActions.state';
import {
  $showBulkDeleteDialog,
  $showBulkMoveDialog,
} from '../../_state/fileDialogs.state';
import { $generateAccessUrl } from '../../_state/fileGenerateAccessUrl.state';
import {
  $exitSelectionMode,
  $selectedFileIds,
  $isSelectionMode,
  $selectAllFiles,
  $files,
} from '../../_state/files.state';
import { useTranslations } from '../../i18n/useTranslations';

export const BulkActionToolbar = () => {
  const t = useTranslations();
  const toast = useToast();
  const files = useAtomValue($files);
  const selectedFileIds = useAtomValue($selectedFileIds);
  const isSelectionMode = useAtomValue($isSelectionMode);
  const exitSelectionMode = useSetAtom($exitSelectionMode);
  const selectAllFiles = useSetAtom($selectAllFiles);
  const setShowBulkDelete = useSetAtom($showBulkDeleteDialog);
  const setShowBulkMove = useSetAtom($showBulkMoveDialog);
  const bulkDownload = useSetAtom($bulkDownloadFiles);
  const generateAccessUrl = useSetAtom($generateAccessUrl);

  const selectedCount = selectedFileIds.size;
  const [isDownloading, setIsDownloading] = React.useState(false);
  const allSelected = selectedFileIds.size === files.length && files.length > 0;

  const handleSelectAllToggle = () => {
    if (allSelected) {
      exitSelectionMode();
    } else {
      selectAllFiles();
    }
  };

  const handleBulkDownload = async () => {
    setIsDownloading(true);

    try {
      const fileIds = Array.from(selectedFileIds);

      // Single file: direct download (no zip)
      if (fileIds.length === 1) {
        const result = await generateAccessUrl({
          fileIds: fileIds,
          expirySeconds: 3600, // 1 hour
        });

        const accessUrl = result.filesGenerateFileAccessURL?.results?.[0];

        if (accessUrl?.url) {
          window.open(accessUrl.url, '_blank');
          toast.success(t.bulkActions.downloadingFile);
        } else {
          toast.error(
            accessUrl?.error ||
              t.bulkActions.failedToDownload.replace('{error}', ''),
          );
        }
      } else {
        // Multiple files: create zip archive
        const timestamp = new Date().toISOString();
        const archiveName = `files-${timestamp}.zip`;

        const result = await bulkDownload({
          fileIds: fileIds,
          archiveName,
        });

        const downloadUrl = result.filesCreateZipArchive?.downloadUrl as
          | string
          | undefined;
        if (downloadUrl) {
          window.open(downloadUrl, '_blank');
          toast.success(
            t.bulkActions.downloadingArchive
              .replace('{count}', selectedCount.toString())
              .replace('{name}', archiveName),
          );
        }
      }
    } catch (error) {
      console.error('Failed to download:', error);
      toast.error(
        t.bulkActions.failedToDownload.replace(
          '{error}',
          error instanceof Error ? error.message : '',
        ),
      );
    } finally {
      setIsDownloading(false);
    }
  };

  // Show toolbar when in selection mode OR when files are selected
  const isVisible = isSelectionMode || selectedCount > 0;

  if (!isVisible) return null;

  return (
    <FloatingToolbar
      position="bottom"
      visible={isVisible}
      aria-label={t.bulkActions.bulkFileActionsAriaLabel}
      className="@BulkActionToolbar"
    >
      <span className="text-sm font-medium">
        {selectedCount !== 1
          ? t.bulkActions.itemsSelected.replace(
              '{count}',
              selectedCount.toString(),
            )
          : t.bulkActions.itemSelected.replace(
              '{count}',
              selectedCount.toString(),
            )}
      </span>

      <Button
        size="small"
        variant="outline"
        icon={<CheckSquare className="size-4" />}
        onPress={handleSelectAllToggle}
      >
        {allSelected ? t.bulkActions.deselectAll : t.bulkActions.selectAll}
      </Button>

      <Button
        size="small"
        variant="outline"
        icon={<Download className="size-4" />}
        onPress={handleBulkDownload}
        isLoading={isDownloading}
        isDisabled={isDownloading}
      >
        {t.bulkActions.download}
      </Button>

      <Button
        size="small"
        variant="outline"
        icon={<Move className="size-4" />}
        onPress={() => setShowBulkMove(true)}
      >
        {t.bulkActions.moveTo}
      </Button>

      <Button
        size="small"
        variant="destructive"
        icon={<Trash2 className="size-4" />}
        onPress={() => setShowBulkDelete(true)}
      >
        {t.bulkActions.delete}
      </Button>

      <Button
        size="small"
        variant="ghost"
        icon={<X className="size-4" />}
        onPress={exitSelectionMode}
        aria-label={t.bulkActions.clearSelectionAriaLabel}
      />
    </FloatingToolbar>
  );
};
